import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd'
import { ArrowLeftOutlined, DownOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { Link, useParams } from 'react-router-dom'
import { adminApi, formatApiError } from '../../api'
import {
  DocCategoryParentBrowseField,
  PageHeader,
  MAX_DOC_CATEGORY_DEPTH,
  collectDocCategoryDescendantIds,
  docCategoryDepth,
  docCategorySubtreeHeight,
  flattenDocCategoryTree,
  type DocCategoryTreeNode,
} from '../../components/ui'

type RuleDetail = {
  id: number
  name: string
  description: string
}

type EditForm = {
  parent_id: number | null
  name: string
  description: string
  preset_tags: string
  is_active: boolean
}

const PROMPT_MIN_LEN = 20
const PROMPT_MAX_LEN = 1000

type AiGenerateJob = {
  job_id: string
  status: string
  progress: number
  status_message: string
  error_message?: string | null
  created_count: number
  skipped: number
  used_fallback: boolean
  parent_path?: string
}

function remainingDepthUnder(parent: DocCategoryTreeNode | null) {
  const parentDepth = parent ? docCategoryDepth(parent.path) : 0
  return Math.max(1, MAX_DOC_CATEGORY_DEPTH - parentDepth)
}

function buildPromptContextDisplay(
  parent: DocCategoryTreeNode | null,
  flat: DocCategoryTreeNode[],
) {
  if (!parent) {
    return { anchor: '（顶级）', siblings: [] as string[], children: [] as string[] }
  }
  return {
    anchor: parent.path || parent.name,
    siblings: flat
      .filter((n) => n.parent_id === parent.parent_id && n.id !== parent.id)
      .map((n) => n.name),
    children: flat.filter((n) => n.parent_id === parent.id).map((n) => n.name),
  }
}

export default function ClassificationRuleDetail() {
  const { ruleId } = useParams()
  const rid = Number(ruleId)
  const [rule, setRule] = useState<RuleDetail | null>(null)
  const [categories, setCategories] = useState<DocCategoryTreeNode[]>([])
  const [catLoading, setCatLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [editing, setEditing] = useState<DocCategoryTreeNode | null>(null)
  const [form] = Form.useForm<EditForm>()
  const [aiPromptOpen, setAiPromptOpen] = useState(false)
  const [aiPromptParent, setAiPromptParent] = useState<DocCategoryTreeNode | null>(null)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiMaxDepth, setAiMaxDepth] = useState(3)
  const [aiJobId, setAiJobId] = useState<string | null>(null)
  const [aiJob, setAiJob] = useState<AiGenerateJob | null>(null)

  const loadRule = useCallback(() => {
    if (!Number.isFinite(rid)) return Promise.resolve()
    return adminApi
      .get<RuleDetail>(`/admin/classification/rules/${rid}`)
      .then((r) => setRule(r.data))
      .catch((e) => message.error(formatApiError(e, '加载规则失败')))
  }, [rid])

  const loadCategories = useCallback(() => {
    if (!Number.isFinite(rid)) return Promise.resolve()
    setCatLoading(true)
    return adminApi
      .get<{ items: DocCategoryTreeNode[] }>('/admin/knowledge/doc-categories', {
        params: { active_only: false, tree: true, rule_id: rid },
      })
      .then((r) => setCategories(r.data.items || []))
      .catch((e) => message.error(formatApiError(e, '加载类型失败')))
      .finally(() => setCatLoading(false))
  }, [rid])

  useEffect(() => {
    void loadRule()
    void loadCategories()
  }, [loadRule, loadCategories])

  useEffect(() => {
    if (!Number.isFinite(rid)) return
    adminApi
      .get<{ job: AiGenerateJob | null }>('/admin/knowledge/doc-categories/batch-generate/jobs/active', {
        params: { rule_id: rid },
      })
      .then((r) => {
        const job = r.data.job
        if (job && (job.status === 'pending' || job.status === 'running')) {
          setAiJobId(job.job_id)
          setAiJob(job)
        }
      })
      .catch(() => {})
  }, [rid])

  useEffect(() => {
    if (!aiJobId) {
      setAiJob(null)
      return
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    const tick = () => {
      if (cancelled) return
      adminApi
        .get<AiGenerateJob>(`/admin/knowledge/doc-categories/batch-generate/jobs/${aiJobId}`)
        .then((r) => {
          if (cancelled) return
          setAiJob(r.data)
          if (r.data.status === 'pending' || r.data.status === 'running') {
            timer = setTimeout(tick, 2000)
            return
          }
          if (r.data.status === 'completed') {
            const hint = r.data.used_fallback ? '（模型未就绪，已使用默认结构）' : ''
            message.success(
              r.data.status_message ||
                `已 AI 生成 ${r.data.created_count} 个子类型${hint}` +
                  (r.data.skipped ? `（跳过 ${r.data.skipped} 个重名）` : ''),
            )
            setAiJobId(null)
            setAiJob(null)
            void loadCategories()
          } else if (r.data.status === 'failed') {
            message.error(r.data.error_message || r.data.status_message || 'AI 生成失败', 8)
            setAiJobId(null)
            setAiJob(null)
          }
        })
        .catch((e) => {
          if (!cancelled) {
            message.error(formatApiError(e, '查询 AI 生成进度失败'))
            setAiJobId(null)
            setAiJob(null)
          }
        })
    }

    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [aiJobId, loadCategories])

  const flatItems = useMemo(() => flattenDocCategoryTree(categories), [categories])
  const parentExcludeIds = useMemo(() => {
    const exclude = new Set<number>()
    if (editing) {
      exclude.add(editing.id)
      for (const id of collectDocCategoryDescendantIds(editing)) exclude.add(id)
    }
    return exclude
  }, [editing])

  const editingSubtreeHeight = useMemo(
    () => (editing ? docCategorySubtreeHeight(editing) : 1),
    [editing],
  )

  const aiPromptContext = useMemo(
    () => buildPromptContextDisplay(aiPromptParent, flatItems),
    [aiPromptParent, flatItems],
  )

  const aiPromptMaxDepth = useMemo(
    () => remainingDepthUnder(aiPromptParent),
    [aiPromptParent],
  )

  const openAiPrompt = (parent: DocCategoryTreeNode | null = null) => {
    if (parent && docCategoryDepth(parent.path) >= MAX_DOC_CATEGORY_DEPTH) {
      message.warning(`文档类型层级最多 ${MAX_DOC_CATEGORY_DEPTH} 级，无法继续新增子类型`)
      return
    }
    setAiPromptParent(parent)
    setAiPrompt('')
    setAiMaxDepth(Math.min(3, remainingDepthUnder(parent)))
    setAiPromptOpen(true)
  }

  const submitAiPrompt = () => {
    const text = aiPrompt.trim()
    if (text.length < PROMPT_MIN_LEN) {
      message.warning(`分类说明不少于 ${PROMPT_MIN_LEN} 字`)
      return
    }
    if (text.length > PROMPT_MAX_LEN) {
      message.warning(`分类说明不超过 ${PROMPT_MAX_LEN} 字`)
      return
    }
    if (aiJobId) return
    adminApi
      .post<AiGenerateJob>(
        '/admin/knowledge/doc-categories/batch-generate',
        {
          user_prompt: text,
          max_depth: aiMaxDepth,
          parent_id: aiPromptParent?.id ?? null,
          rule_name: rule?.name || '',
        },
        { params: { rule_id: rid } },
      )
      .then((r) => {
        setAiPromptOpen(false)
        setAiJobId(r.data.job_id)
        setAiJob(r.data)
        message.info('已提交 AI 新建任务，请稍候…')
      })
      .catch((e) => message.error(formatApiError(e, '提交 AI 新建失败')))
  }

  const renderCreateTypeMenu = () => ({
    items: [
      {
        key: 'manual',
        label: '手动新建',
        onClick: () => openCreate(),
      },
      {
        key: 'ai-new',
        label: 'AI 新建',
        disabled: Boolean(aiJobId),
        onClick: () => openAiPrompt(null),
      },
    ],
  })

  const renderAddChildMenu = (parent: DocCategoryTreeNode | null, disabled: boolean) => ({
    items: [
      {
        key: 'manual',
        label: '手动新增',
        disabled,
        onClick: () => openCreate(parent ?? undefined),
      },
      {
        key: 'ai',
        label: 'AI 新建',
        disabled: disabled || Boolean(aiJobId),
        onClick: () => openAiPrompt(parent),
      },
    ],
  })

  const openCreate = (parent?: DocCategoryTreeNode) => {
    if (parent && docCategoryDepth(parent.path) >= MAX_DOC_CATEGORY_DEPTH) {
      message.warning(`文档类型层级最多 ${MAX_DOC_CATEGORY_DEPTH} 级，无法继续新增子类型`)
      return
    }
    setEditing(null)
    form.setFieldsValue({
      parent_id: parent?.id ?? null,
      name: '',
      description: '',
      preset_tags: '',
      is_active: true,
    })
    setModalOpen(true)
  }

  const openEdit = (row: DocCategoryTreeNode) => {
    setEditing(row)
    form.setFieldsValue({
      parent_id: row.parent_id ?? null,
      name: row.name,
      description: row.description || '',
      preset_tags: row.preset_tags || '',
      is_active: row.is_active,
    })
    setModalOpen(true)
  }

  const suggestByAi = () => {
    const name = String(form.getFieldValue('name') || '').trim()
    if (!name) {
      message.warning('请先填写类型名称')
      return
    }
    setSuggesting(true)
    adminApi
      .post<{ description: string; preset_tags: string }>('/admin/knowledge/doc-categories/suggest', { name })
      .then((r) => {
        form.setFieldsValue({
          description: r.data.description || '',
          preset_tags: r.data.preset_tags || '',
        })
        message.success('已生成类型说明与预置标签')
      })
      .catch((e) => message.error(formatApiError(e, 'AI 生成失败')))
      .finally(() => setSuggesting(false))
  }

  const saveCategory = () => {
    void form.validateFields().then((v) => {
      setSaving(true)
      const body = {
        parent_id: v.parent_id ?? null,
        name: v.name.trim(),
        description: (v.description || '').trim(),
        preset_tags: (v.preset_tags || '').trim(),
        sort_order: editing?.sort_order ?? flatItems.length,
        is_active: v.is_active,
      }
      const req = editing
        ? adminApi.put(`/admin/knowledge/doc-categories/${editing.id}`, body)
        : adminApi.post('/admin/knowledge/doc-categories', body, { params: { rule_id: rid } })
      req
        .then(() => {
          message.success(editing ? '已保存' : '已创建')
          setModalOpen(false)
          void loadCategories()
        })
        .catch((e) => message.error(formatApiError(e, '保存失败')))
        .finally(() => setSaving(false))
    })
  }

  if (!Number.isFinite(rid)) {
    return <Typography.Text type="danger">无效的规则 ID</Typography.Text>
  }

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Link to="/manage/classification">
          <Button icon={<ArrowLeftOutlined />}>返回智能分类</Button>
        </Link>
      </Space>
      <PageHeader
        title={rule?.name || '文档类型'}
        description={
          rule?.description ||
          `维护本规则下的文档类型树（最多 ${MAX_DOC_CATEGORY_DEPTH} 级）；虚拟分类请在智能分类列表中创建。`
        }
      />
      <Card>
        {aiJob && (aiJob.status === 'pending' || aiJob.status === 'running') ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="文档类型 AI 生成进行中"
            description={
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text type="secondary">
                  {aiJob.parent_path ? `上级：${aiJob.parent_path}` : '顶级类型'}
                  {' · '}
                  {aiJob.status_message || '处理中…'}
                </Typography.Text>
                <Progress percent={aiJob.progress} size="small" status="active" />
              </Space>
            }
          />
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Dropdown menu={renderCreateTypeMenu()}>
            <Button type="primary" icon={<PlusOutlined />}>
              新建类型 <DownOutlined />
            </Button>
          </Dropdown>
        </div>
        <Table<DocCategoryTreeNode>
          rowKey="id"
          loading={catLoading}
          dataSource={categories}
          pagination={false}
          expandable={{ defaultExpandAllRows: true }}
          columns={[
            {
              title: '类型名称',
              dataIndex: 'name',
              width: 220,
              ellipsis: true,
              render: (name: string) => <span title={name}>{name}</span>,
            },
            { title: '完整路径', dataIndex: 'path', ellipsis: true, render: (t: string) => t || '—' },
            { title: '说明', dataIndex: 'description', ellipsis: true, render: (t: string) => t || '—' },
            {
              title: '操作',
              width: 300,
              render: (_, row) => {
                const atMaxDepth = docCategoryDepth(row.path) >= MAX_DOC_CATEGORY_DEPTH
                return (
                  <Space wrap>
                    <Button size="small" onClick={() => openEdit(row)}>
                      编辑
                    </Button>
                    <Dropdown menu={renderAddChildMenu(row, atMaxDepth)}>
                      <Button size="small" disabled={atMaxDepth}>
                        新增子类型 <DownOutlined />
                      </Button>
                    </Dropdown>
                    <Button
                      type="link"
                      danger
                      size="small"
                      disabled={Boolean(row.children?.length)}
                      onClick={() => {
                        Modal.confirm({
                          title: `删除文档类型「${row.path || row.name}」？`,
                          okText: '删除',
                          okButtonProps: { danger: true },
                          cancelText: '取消',
                          onOk: () =>
                            adminApi
                              .delete(`/admin/knowledge/doc-categories/${row.id}`)
                              .then(() => {
                                message.success('已删除')
                                void loadCategories()
                              })
                              .catch((e) => {
                                message.error(formatApiError(e, '删除失败'))
                                return Promise.reject(e)
                              }),
                        })
                      }}
                    >
                      删除
                    </Button>
                  </Space>
                )
              },
            },
          ]}
        />
      </Card>

      <Modal
        title={editing ? '编辑文档类型' : '新建文档类型'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={saveCategory}
        confirmLoading={saving}
        okText={editing ? '保存' : '创建'}
        cancelText="取消"
        destroyOnHidden
        width={560}
      >
        <Form form={form} layout="vertical" initialValues={{ parent_id: null, is_active: true }}>
          <Form.Item
            name="parent_id"
            label="上级类型"
            extra={`文档类型树最多 ${MAX_DOC_CATEGORY_DEPTH} 级（含顶级）`}
          >
            <DocCategoryParentBrowseField
              tree={categories}
              excludeIds={parentExcludeIds}
              subtreeHeight={editingSubtreeHeight}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="类型名称"
            rules={[
              { required: true, message: '请输入类型名称' },
              { max: 20, message: '类型名称不超过 20 个字' },
            ]}
          >
            <Input maxLength={20} showCount placeholder="如：制度、人事制度（不超过 20 字）" />
          </Form.Item>
          <Form.Item
            label={
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <span>类型说明</span>
                <Button type="link" size="small" icon={<ThunderboltOutlined />} loading={suggesting} onClick={suggestByAi}>
                  AI 生成
                </Button>
              </Space>
            }
            name="description"
          >
            <Input.TextArea rows={3} maxLength={512} placeholder="描述该类型适用场景" />
          </Form.Item>
          <Form.Item name="preset_tags" label="预置标签">
            <Input maxLength={512} placeholder="逗号分隔，如：制度,人事,考勤" />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={aiPromptParent ? 'AI 新建子类型' : 'AI 新建类型'}
        open={aiPromptOpen}
        onCancel={() => setAiPromptOpen(false)}
        onOk={submitAiPrompt}
        okText="生成"
        cancelText="取消"
        okButtonProps={{ disabled: Boolean(aiJobId) || aiPrompt.trim().length < PROMPT_MIN_LEN }}
        destroyOnHidden
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {aiPromptParent ? (
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              上级：{aiPromptContext.anchor}
              {aiPromptContext.siblings.length > 0
                ? ` · 同级：${aiPromptContext.siblings.join('、')}`
                : ''}
              {aiPromptContext.children.length > 0
                ? ` · 已有：${aiPromptContext.children.join('、')}`
                : ''}
            </Typography.Text>
          ) : null}
          <div>
            <Typography.Text style={{ display: 'block', marginBottom: 6 }}>分类说明</Typography.Text>
            <Input.TextArea
              rows={5}
              maxLength={PROMPT_MAX_LEN}
              showCount
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={
                aiPromptParent
                  ? '希望补充的子分类及适用场景…'
                  : '描述分类场景，如部门、文档类型…'
              }
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {PROMPT_MIN_LEN}～{PROMPT_MAX_LEN} 字
            </Typography.Text>
          </div>
          <div>
            <Space align="center" style={{ marginBottom: 4 }}>
              <Typography.Text>生成深度</Typography.Text>
              <InputNumber
                min={1}
                max={aiPromptMaxDepth}
                value={Math.min(aiMaxDepth, aiPromptMaxDepth)}
                onChange={(v) => setAiMaxDepth(v ?? 1)}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                级
              </Typography.Text>
            </Space>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {aiMaxDepth === 1
                ? '仅生成本层直接子分类'
                : `示例：${Array.from({ length: Math.min(aiMaxDepth, 3) }, (_, i) =>
                    ['一级', '二级', '三级', '四级', '五级'][i],
                  ).join(' → ')}${aiMaxDepth > 3 ? ' → …' : ''}`}
              （整棵类型树系统上限 {MAX_DOC_CATEGORY_DEPTH} 级）
            </Typography.Text>
          </div>
        </Space>
      </Modal>
    </div>
  )
}
